import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Autocomplete, Box, Button, Checkbox, TextField } from "@mui/material";
import type { Dispatch } from "react";

import { GetActivityTypesQuery } from "@/graphql/graphql";
import { ACTIVITY_CATEGORIES, ACTIVITY_DIFFICULTIES } from "@/utils/consts";
import { formatEnumLabel } from "@/utils/format";

import {
  ActivitiesQueryAction,
  ActivitiesQueryState,
} from "./ActivitiesTable.activitiesQueryState";
import {
  clearAllFiltersButtonSx,
  compactFilterTextFieldSx,
  filterClearButtonWrapperSx,
  filterControlSx,
  filterRowSx,
} from "./ActivitiesTable.styles";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const multiSelectAutocompleteProps = {
  multiple: true as const,
  disableCloseOnSelect: true,
  size: "small" as const,
  limitTags: 1,
};

interface ActivitiesTableFiltersProps {
  titleSearchInput: string;
  onTitleSearchInputChange: (value: string) => void;
  contentSearchInput: string;
  onContentSearchInputChange: (value: string) => void;
  queryState: ActivitiesQueryState;
  dispatchQuery: Dispatch<ActivitiesQueryAction>;
  filtersReady: boolean;
  sortedActivityTypes: GetActivityTypesQuery["activityTypes"];
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;
}

const ActivitiesTableFilters: React.FC<ActivitiesTableFiltersProps> = ({
  titleSearchInput,
  onTitleSearchInputChange,
  contentSearchInput,
  onContentSearchInputChange,
  queryState,
  dispatchQuery,
  filtersReady,
  sortedActivityTypes,
  hasActiveFilters,
  onClearAllFilters,
}) => (
  <Box sx={filterRowSx}>
    <Box sx={filterControlSx}>
      <TextField
        size="small"
        fullWidth
        label="Title"
        placeholder="Search by title"
        value={titleSearchInput}
        onChange={(e) => onTitleSearchInputChange(e.target.value)}
        sx={compactFilterTextFieldSx}
      />
    </Box>
    <Box sx={filterControlSx}>
      <TextField
        size="small"
        fullWidth
        label="Content"
        placeholder="Search activity content"
        value={contentSearchInput}
        onChange={(e) => onContentSearchInputChange(e.target.value)}
        sx={compactFilterTextFieldSx}
      />
    </Box>
    <Box sx={filterControlSx}>
      <Autocomplete
        {...multiSelectAutocompleteProps}
        disabled={!filtersReady}
        options={ACTIVITY_CATEGORIES}
        value={queryState.categories || []}
        onChange={(_, value) => dispatchQuery({ type: "SET_CATEGORIES", categories: value })}
        getOptionLabel={(option) => formatEnumLabel(option)}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={checkboxIcon}
              checkedIcon={checkboxCheckedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {formatEnumLabel(option)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Category"
            placeholder="All categories"
            sx={compactFilterTextFieldSx}
          />
        )}
      />
    </Box>
    <Box sx={filterControlSx}>
      <Autocomplete
        {...multiSelectAutocompleteProps}
        disabled={!filtersReady}
        options={sortedActivityTypes.map((t) => t.activityTypeKey)}
        value={queryState.typeKeys || []}
        onChange={(_, value) => dispatchQuery({ type: "SET_TYPE_KEYS", typeKeys: value })}
        getOptionLabel={(key) =>
          sortedActivityTypes.find((t) => t.activityTypeKey === key)?.activityTypeLabel ?? key
        }
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={checkboxIcon}
              checkedIcon={checkboxCheckedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {sortedActivityTypes.find((t) => t.activityTypeKey === option)?.activityTypeLabel ??
              option}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Types"
            placeholder="All types"
            sx={compactFilterTextFieldSx}
          />
        )}
      />
    </Box>
    <Box sx={filterControlSx}>
      <Autocomplete
        {...multiSelectAutocompleteProps}
        disabled={!filtersReady}
        options={ACTIVITY_DIFFICULTIES}
        value={queryState.difficulties || []}
        onChange={(_, value) => dispatchQuery({ type: "SET_DIFFICULTIES", difficulties: value })}
        getOptionLabel={(option) => formatEnumLabel(option)}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={checkboxIcon}
              checkedIcon={checkboxCheckedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {formatEnumLabel(option)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Difficulty"
            placeholder="All difficulties"
            sx={compactFilterTextFieldSx}
          />
        )}
      />
    </Box>
    <Box sx={filterClearButtonWrapperSx}>
      <Button
        variant="outlined"
        size="small"
        disabled={!filtersReady || !hasActiveFilters}
        onClick={onClearAllFilters}
        sx={clearAllFiltersButtonSx}
      >
        Clear filters
      </Button>
    </Box>
  </Box>
);

export default ActivitiesTableFilters;
